// Client-level Billing Tab — mirrors Matter BillingTab
// Sub-tabs: Overview, Time, Expenses, Transactions, Invoices, Trust
// Uses useClientBilling (client_id scope) instead of useMatterBilling (matter_id)

import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { Contact } from '../../../../config/api';
import type { useClientBilling } from '../../hooks/useClientBilling';
import { TRUST_TRANSACTION_TYPE_LABELS } from '../../../billing/types/billing.types';

// Sub-tab sections
import ClientBillingOverview from './billing-sections/ClientBillingOverview';
import ClientTimeSection from './billing-sections/ClientTimeSection';
import ClientExpensesSection from './billing-sections/ClientExpensesSection';

// Reuse Matter-level sections that don't need matter-specific logic
import TransactionsFeedSection from '../../../matters/components/matter-detail-tabs/billing-sections/TransactionsFeedSection';
import InvoicesSection from '../../../matters/components/matter-detail-tabs/billing-sections/InvoicesSection';

// ─── Types ──────────────────────────────────────────────────────

interface BillingTabProps {
  contact: Contact;
  billing: ReturnType<typeof useClientBilling>;
}

type BillingSection = 'overview' | 'time' | 'expenses' | 'transactions' | 'invoices' | 'trust';

const BILLING_SECTIONS: { key: BillingSection; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'time', label: 'Time' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'trust', label: 'Trust' },
];

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  dateString
    ? new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Not dated';

const titleCase = (value?: string | null) => {
  if (!value) return 'Unknown';
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

// ─── Trust sub-section (inline since client trust is read-only aggregate) ───

const ClientTrustSection: React.FC<{
  trustLedgers: ReturnType<typeof useClientBilling>['trustLedgers'];
  trustTransactions: ReturnType<typeof useClientBilling>['trustTransactions'];
  stats: ReturnType<typeof useClientBilling>['stats'];
  loading: boolean;
}> = ({ trustLedgers, trustTransactions, stats, loading }) => {
  const safeTrustLedgers = Array.isArray(trustLedgers) ? trustLedgers : [];
  const safeTrustTransactions = Array.isArray(trustTransactions) ? trustTransactions : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-6 w-6 border-2 border-teal-500 border-t-transparent rounded-full" />
        <span className="ml-2 text-sm text-gray-500">Loading trust data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
        <svg className="h-5 w-5 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
        </svg>
        Trust Accounts (IOLTA)
      </h4>

      {/* Aggregate trust summary */}
      {safeTrustLedgers.length > 0 ? (
        <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-teal-600 dark:text-teal-400 font-medium">Total Balance</div>
              <div className="text-lg font-bold text-teal-900 dark:text-teal-100 tabular-nums">
                {formatCurrency(stats.trustBalance)}
              </div>
            </div>
            <div>
              <div className="text-xs text-teal-600 dark:text-teal-400 font-medium">Total Deposits</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                {formatCurrency(stats.trustTotalDeposits)}
              </div>
            </div>
            <div>
              <div className="text-xs text-teal-600 dark:text-teal-400 font-medium">Total Disbursements</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">
                {formatCurrency(stats.trustTotalDisbursements)}
              </div>
            </div>
            <div>
              <div className="text-xs text-teal-600 dark:text-teal-400 font-medium">Trust Ledgers</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                {safeTrustLedgers.length}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                across {safeTrustLedgers.length} matter{safeTrustLedgers.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          No trust ledgers found for this client's matters.
        </div>
      )}

      {/* Per-ledger breakdown if multiple */}
      {safeTrustLedgers.length > 1 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Per-Matter Breakdown</p>
          {safeTrustLedgers.map((ledger) => (
            <div key={ledger.client_trust_ledger_id} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="min-w-0">
                <p className="text-sm text-gray-900 dark:text-white truncate">
                  {(ledger as any).matter?.description || (ledger as any).matter?.matter_number || ledger.matter_id?.slice(0, 8) || 'General'}
                </p>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0 ml-3">
                <span className="text-sm font-bold text-teal-700 dark:text-teal-300 tabular-nums">
                  {formatCurrency(Number(ledger.current_balance))}
                </span>
                <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                  ledger.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {titleCase(ledger.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Trust Transactions Table */}
      {safeTrustTransactions.length > 0 && (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Type</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Source / Payee</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Balance</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {safeTrustTransactions.map((txn, i) => (
                <tr key={txn.trust_transaction_id} className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50" style={{ '--row-index': i } as React.CSSProperties}>
                  <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">
                    {formatDate(txn.transaction_date)}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {TRUST_TRANSACTION_TYPE_LABELS[txn.transaction_type] || txn.transaction_type}
                  </td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">
                    {txn.description}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {txn.source_or_payee || '-'}
                  </td>
                  <td className={`px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap ${
                    txn.direction === 'credit'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {txn.direction === 'credit' ? '+' : '-'}{formatCurrency(Number(txn.amount))}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium tabular-nums whitespace-nowrap">
                    {formatCurrency(Number(txn.balance_after))}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                      txn.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                      txn.status === 'reversed' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300' :
                      txn.status === 'void' ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                    }`}>
                      {titleCase(txn.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Main Client Billing Tab ────────────────────────────────────

const BillingTab: React.FC<BillingTabProps> = ({ contact, billing }) => {
  const [activeSection, setActiveSection] = useState<BillingSection>('overview');
  const [contentVisible, setContentVisible] = useState(true);
  const [renderKey, setRenderKey] = useState(0);

  // Sliding indicator state
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  // Update sliding indicator position
  useEffect(() => {
    if (!tabBarRef.current) return;
    const activeBtn = tabBarRef.current.querySelector<HTMLButtonElement>('[data-active="true"]');
    if (activeBtn) {
      const barRect = tabBarRef.current.getBoundingClientRect();
      const btnRect = activeBtn.getBoundingClientRect();
      setIndicator({ left: btnRect.left - barRect.left, width: btnRect.width });
    }
  }, [activeSection]);

  const switchSection = useCallback((section: BillingSection) => {
    if (section === activeSection) return;
    setContentVisible(false);
    setTimeout(() => {
      setActiveSection(section);
      setRenderKey((k) => k + 1);
      requestAnimationFrame(() => setContentVisible(true));
    }, 150);
  }, [activeSection]);

  const {
    expenses, timeEntries, bills,
    trustLedgers, trustTransactions,
    loading, stats,
  } = billing;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Billing</h3>
      </div>

      {/* Sub-tab navigation with sliding indicator */}
      <div className="relative flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit" ref={tabBarRef}>
        {BILLING_SECTIONS.map((section) => (
          <button
            key={section.key}
            data-active={activeSection === section.key}
            onClick={() => switchSection(section.key)}
            className={`relative z-10 px-3 py-1.5 text-sm rounded-md transition-colors duration-150 ${
              activeSection === section.key
                ? 'text-lex-accent font-medium'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {section.label}
          </button>
        ))}
        {/* Sliding active indicator */}
        <div
          className="absolute top-1 bottom-1 rounded-md bg-white dark:bg-gray-700 shadow-sm transition-all duration-300"
          style={{ left: indicator.left, width: indicator.width, transitionTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }}
        />
      </div>

      {/* Loading indicator during crossfade */}
      {!contentVisible && (
        <div className="flex items-center justify-center py-4">
          <div className="h-4 w-4 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Active section with crossfade */}
      <div
        key={renderKey}
        style={{
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 150ms ease-out',
        }}
      >
        {activeSection === 'overview' && (
          <ClientBillingOverview stats={stats} />
        )}

        {activeSection === 'time' && (
          <ClientTimeSection
            timeEntries={timeEntries}
            loading={loading}
          />
        )}

        {activeSection === 'expenses' && (
          <ClientExpensesSection
            expenses={expenses}
            loading={loading}
          />
        )}

        {activeSection === 'transactions' && (
          <TransactionsFeedSection
            expenses={expenses}
            timeEntries={timeEntries}
            trustTransactions={trustTransactions}
            loading={loading}
          />
        )}

        {activeSection === 'invoices' && (
          <InvoicesSection
            bills={bills}
            loading={loading}
            createManualBill={async () => {
              // Client-level: no auto-create — placeholder that shows instruction
              throw new Error('Create invoices from individual matter pages');
            }}
          />
        )}

        {activeSection === 'trust' && (
          <ClientTrustSection
            trustLedgers={trustLedgers}
            trustTransactions={trustTransactions}
            stats={stats}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

export default BillingTab;
